import supabase from '../lib/supabase';

const APPOINTMENT_SELECT_FIELDS = `
  id,
  patient_id,
  doctor_id,
  appointment_date,
  duration_minutes,
  consultation_type,
  status,
  notes,
  is_paid,
  payment_intent_id,
  practice_id,
  status_reason,
  superseded_by_appointment_id,
  diagnosis_title,
  diagnosis_notes,
  booked_service_id,
  booked_service_name,
  booked_service_price,
  booked_service_currency,
  care_subject_type,
  care_subject_dependant_id,
  care_subject_name,
  care_subject_relationship,
  care_subject_date_of_birth,
  guardian_user_id,
  booked_by_user_id,
  responsible_user_id,
  responsible_relationship
`;

async function findOrCreatePatientId(userId) {
  const { data, error } = await supabase?.rpc('ensure_patient_exists', {
    user_uuid: userId || null,
  });

  if (error) {
    throw error;
  }

  if (!data) {
    throw new Error('Patient record not found and could not be created');
  }

  return data;
}

async function resolveDoctorUserId(doctorId) {
  const rawDoctorId = String(doctorId || '').trim();
  if (!rawDoctorId) {
    throw new Error('Doctor ID is required');
  }

  try {
    const { data: byUserId, error: byUserIdError } = await supabase
      ?.from('doctor_profiles')
      ?.select('id, user_id')
      ?.eq('user_id', rawDoctorId)
      ?.limit(1)
      ?.maybeSingle();

    if (!byUserIdError && byUserId?.user_id) {
      return String(byUserId.user_id).trim();
    }

    const { data: byProfileId, error: byProfileIdError } = await supabase
      ?.from('doctor_profiles')
      ?.select('id, user_id')
      ?.eq('id', rawDoctorId)
      ?.limit(1)
      ?.maybeSingle();

    if (!byProfileIdError && byProfileId?.user_id) {
      return String(byProfileId.user_id).trim();
    }
  } catch {
    // Fall back to the provided ID if the normalization lookup is unavailable.
  }

  return rawDoctorId;
}

// Resolve the canonical patient.id for the logged-in user
export async function getCurrentPatientId() {
  try {
    const { data: { user }, error: authErr } = await supabase?.auth?.getUser();
    if (authErr || !user) {
      throw new Error('Not authenticated');
    }

    const ensured = await supabase?.rpc('ensure_patient_exists', {
      user_uuid: user?.id || null,
    });
    if (!ensured?.error && ensured?.data) {
      return ensured.data;
    }

    const fallback = await supabase?.rpc('get_patient_id_from_auth');
    if (!fallback?.error && fallback?.data) {
      return fallback.data;
    }

    return await findOrCreatePatientId(user?.id);
  } catch (error) {
    throw new Error(`Failed to get patient ID: ${error.message}`);
  }
}

// Create appointment with the correct FK
export async function createAppointment({
  doctorId,
  practiceId,
  startsAt,
  note,
  consultationType = 'private',
  duration = 30,
  serviceId = null,
  serviceName = null,
  servicePrice = null,
  serviceCurrency = null,
  careSubject = null,
}) {
  try {
    const {
      data: { user },
      error: authError,
    } = await supabase?.auth?.getUser();
    if (authError || !user?.id) {
      throw authError || new Error('Not authenticated');
    }

    const patientId = await getCurrentPatientId();
    const resolvedDoctorId = await resolveDoctorUserId(doctorId);
    const normalizedCareSubject =
      careSubject && typeof careSubject === 'object' ? careSubject : null;
    
    const appointmentData = {
      patient_id: patientId,
      doctor_id: resolvedDoctorId,
      practice_id: practiceId,
      appointment_date: startsAt,
      duration_minutes: duration,
      consultation_type: consultationType,
      notes: note || null,
      status: 'pending',
      is_paid: false,
      booked_service_id: serviceId || null,
      booked_service_name: serviceName || null,
      booked_service_price: Number.isFinite(Number(servicePrice)) ? Number(servicePrice) : null,
      booked_service_currency: serviceCurrency || null,
      care_subject_type:
        String(normalizedCareSubject?.type || '').trim().toLowerCase() === 'dependant'
          ? 'dependant'
          : 'self',
      care_subject_dependant_id:
        String(normalizedCareSubject?.dependantId || '').trim() || null,
      care_subject_name: String(normalizedCareSubject?.patientName || '').trim() || null,
      care_subject_relationship:
        String(normalizedCareSubject?.relationship || '').trim() || null,
      care_subject_date_of_birth:
        String(normalizedCareSubject?.dateOfBirth || '').trim() || null,
      guardian_user_id: String(normalizedCareSubject?.guardianUserId || user.id || '').trim() || null,
      booked_by_user_id: String(user.id || '').trim() || null,
      responsible_user_id:
        String(normalizedCareSubject?.responsibleUserId || normalizedCareSubject?.guardianUserId || user.id || '').trim() ||
        null,
      responsible_relationship:
        String(normalizedCareSubject?.responsibleRelationship || normalizedCareSubject?.relationship || 'Self').trim() ||
        'Self',
    };

    const { data, error } = await supabase
      ?.from('appointments')
      ?.insert(appointmentData)
      ?.select(
        'id, doctor_id, booked_service_id, booked_service_name, booked_service_price, booked_service_currency, care_subject_type, care_subject_dependant_id, care_subject_name, care_subject_relationship, care_subject_date_of_birth, guardian_user_id, booked_by_user_id, responsible_user_id, responsible_relationship'
      )
      ?.single();

    if (error) {
      throw error;
    }

    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
}

// Fetch appointments (patient view) - FIXED: Use correct doctor relationship
export async function listMyAppointments() {
  try {
    const patientId = await getCurrentPatientId();
    
    // FIXED: Use doctor_id as user_id reference to doctor_profiles
    const { data, error } = await supabase?.from('appointments')?.select(`
        ${APPOINTMENT_SELECT_FIELDS},
        patient:patients(
          id,
          user_id
        ),
        doctor:doctor_profiles!appointments_doctor_id_fkey(
          user_id,
          specialization,
          consultation_fee,
          profile_image_url,
          verified_status,
          profiles!doctor_profiles_user_id_fkey(
            display_name,
            avatar_url,
            phone
          )
        )
      `)?.eq('patient_id', patientId)?.order('appointment_date', { ascending: true });

    if (error) {
      throw error;
    }

    return { data, error: null };
  } catch (error) {
    return { data: [], error };
  }
}

// Fetch appointments (doctor/staff view) – practice-scoped
export async function listPracticeAppointments(practiceId, doctorId = null) {
  try {
    let query = supabase?.from('appointments')?.select(`
        ${APPOINTMENT_SELECT_FIELDS},
        patient:patients(
          id,
          user_id
        ),
        doctor:doctor_profiles(
          id,
          specialization,
          consultation_fee,
          profile_image_url,
          verified_status,
          user_id,
          profiles!inner(
            display_name,
            avatar_url,
            phone
          )
        )
      `)?.eq('practice_id', practiceId);

    if (doctorId) {
      query = query?.eq('doctor_id', doctorId);
    }

    const { data, error } = await query?.order('appointment_date', { ascending: true });

    if (error) {
      throw error;
    }

    return { data, error: null };
  } catch (error) {
    return { data: [], error };
  }
}

// Get single appointment details
export async function getAppointmentDetails(appointmentId) {
  try {
    const { data, error } = await supabase?.from('appointments')?.select(`
        ${APPOINTMENT_SELECT_FIELDS},
        patient:patients(
          id,
          user_id
        ),
        doctor:doctor_profiles(
          id,
          specialization,
          consultation_fee,
          profile_image_url,
          verified_status,
          user_id,
          profiles!inner(
            display_name,
            avatar_url,
            phone
          )
        )
      `)?.eq('id', appointmentId)?.single();

    if (error) {
      throw error;
    }

    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
}

// Update appointment status
export async function updateAppointmentStatus(appointmentId, status, userId) {
  try {
    // First check if user has permission to update this appointment
    const { data: appointment } = await getAppointmentDetails(appointmentId);
    if (!appointment) {
      throw new Error('Appointment not found');
    }

    const { data, error } = await supabase?.from('appointments')?.update({ 
        status,
        updated_at: new Date()?.toISOString()
      })?.eq('id', appointmentId)?.select();

    if (error) {
      throw error;
    }

    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
}

// Cancel appointment
export async function cancelAppointment(appointmentId, reason = null) {
  try {
    const patientId = await getCurrentPatientId();
    
    const { data, error } = await // Ensure patient can only cancel their own appointments
    supabase?.from('appointments')?.update({ 
        status: 'cancelled',
        notes: reason ? `Cancelled: ${reason}` : 'Cancelled by patient',
        updated_at: new Date()?.toISOString()
      })?.eq('id', appointmentId)?.eq('patient_id', patientId)?.select();

    if (error) {
      throw error;
    }

    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
}

// Update appointment payment status
export async function updateAppointmentPayment(appointmentId, paymentIntentId, isPaid = true) {
  try {
    const { data, error } = await supabase?.from('appointments')?.update({ 
        is_paid: isPaid,
        payment_intent_id: paymentIntentId,
        updated_at: new Date()?.toISOString()
      })?.eq('id', appointmentId)?.select();

    if (error) {
      throw error;
    }

    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
}

// Get upcoming appointments for a user - FIXED: Proper doctor relationship
export async function getUpcomingAppointments(limit = 5) {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) {
      throw new Error('Not authenticated');
    }

    let patientId = null;
    try {
      patientId = await getCurrentPatientId();
    } catch {
      patientId = null;
    }

    // Check if user is a doctor
    const { data: doctorData } = await supabase?.from('doctor_profiles')?.select('user_id')?.eq('user_id', user?.id)?.single();

    let query;
    const now = new Date()?.toISOString();

    if (patientId) {
      // User is a patient - FIXED: Use correct doctor relationship
      query = supabase?.from('appointments')?.select(`
          id, 
          appointment_date,
          duration_minutes,
          consultation_type,
          status, 
          notes,
          doctor:doctor_profiles!appointments_doctor_id_fkey(
            specialization,
            verified_status,
            profiles!doctor_profiles_user_id_fkey(
              display_name,
              avatar_url
            )
          )
        `)?.eq('patient_id', patientId)?.gte('appointment_date', now);
    } else if (doctorData) {
      // User is a doctor - FIXED: Use user_id for doctor filtering
      query = supabase?.from('appointments')?.select(`
          id, 
          appointment_date,
          duration_minutes,
          consultation_type,
          status, 
          notes,
          patient:patients(
            user_id,
            user_id
          )
        `)?.eq('doctor_id', user?.id)?.gte('appointment_date', now);
    } else {
      return { data: [], error: null };
    }

    const { data, error } = await query?.order('appointment_date', { ascending: true })?.limit(limit);

    if (error) {
      throw error;
    }

    return { data, error: null };
  } catch (error) {
    return { data: [], error };
  }
}

// Role-based helpers
export async function getUserRole() {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) return null;

    // Check if user is a patient
    try {
      const patientId = await getCurrentPatientId();
      if (patientId) return 'patient';
    } catch {
      // continue
    }

    // Check if user is a doctor
    const { data: doctorData } = await supabase?.from('doctor_profiles')?.select('user_id')?.eq('user_id', user?.id)?.single();

    if (doctorData) return 'doctor';

    // Check admin status using existing function
    const { data: isAdmin } = await supabase?.rpc('is_admin');
    if (isAdmin) return 'admin';

    return 'user';
  } catch (error) {
    return null;
  }
}

export default {
  getCurrentPatientId,
  createAppointment,
  listMyAppointments,
  listPracticeAppointments,
  getAppointmentDetails,
  updateAppointmentStatus,
  cancelAppointment,
  updateAppointmentPayment,
  getUpcomingAppointments,
  getUserRole
};
